/**
 * /claude-harness — The Production Harness course home.
 *
 * Claude Code configuration, skills, hooks, and the session lifecycle. Lists the six modules with
 * lock badges (HRN.1 free, HRN.2–6 gated). Content served by the isolated
 * /api/v1/claude-harness router.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getClaudeHarnessCurriculum } from "../api/client";
import HarnessAccessCTA from "../components/harness/HarnessAccessCTA";
import HarnessChangelog from "../components/harness/HarnessChangelog";

export default function ClaudeHarnessHome() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const {
    data: curriculum,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["claude-harness-curriculum"],
    queryFn: getClaudeHarnessCurriculum,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !curriculum) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{t("ClaudeHarnessHome.errorLoad")}</Alert>
      </Container>
    );
  }

  const modules = curriculum.milestones.flatMap((m) => m.modules);
  const first = modules[0];
  // Ownership is INFERRED from the server's own gating rather than from a
  // plan_tier read on the client: a gated module that came back unlocked means
  // the backend let this viewer in, by whichever of D-012's two doors. That
  // keeps this page correct without teaching it the access rules a second time.
  const owns = modules.some((m) => m.tier !== "free" && !m.locked);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography
        variant="overline"
        sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.12em" }}
      >
        {t("ClaudeHarnessHome.eyebrow")}
      </Typography>
      <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
        {curriculum.title}
      </Typography>
      <Typography variant="h6" fontWeight={400} color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.5 }}>
        {curriculum.subtitle}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 680 }}>
        <Trans
          t={t}
          i18nKey="ClaudeHarnessHome.prereqBody"
          components={{
            strong: <strong />,
            mastery: <RouterLink to="/agentic-ai-mastery" />,
          }}
        />
      </Typography>

      {/* THESIS — what the course is actually about, before any syllabus. */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, maxWidth: 720 }}>
          {t("ClaudeHarnessHome.thesisTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.7 }}>
          {t("ClaudeHarnessHome.thesisBody")}
        </Typography>
      </Box>

      {/* SCORECARD — the free diagnostic, placed BEFORE any price is mentioned.
          Value first, then price: a $149 figure shown before the reader knows
          what is missing from their setup is an unanchored objection. */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mt: 5,
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
          <AssessmentOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography
            variant="overline"
            sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
          >
            {t("ClaudeHarnessHome.scoreEyebrow")}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
          {t("ClaudeHarnessHome.scoreTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 680, lineHeight: 1.65 }}>
          {t("ClaudeHarnessHome.scoreBody")}
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 3 }}>
          {[1, 2, 3].map((n) => (
            <Stack key={n} direction="row" spacing={1.25} alignItems="flex-start">
              <CheckCircleOutlineIcon sx={{ fontSize: 17, color: theme.palette.primary.main, mt: "3px" }} />
              <Typography variant="body2" color="text.secondary">
                {t(`ClaudeHarnessHome.scoreBullet${n}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          component={RouterLink}
          to="/claude-harness/scorecard"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          {t("ClaudeHarnessHome.scoreCta")}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          {t("ClaudeHarnessHome.scoreFootnote")}
        </Typography>
      </Paper>

      {/* FAILURES — the spine. Each one is a real documented incident from the
          field notes behind the course, mapped to the module that prevents it.
          This is what makes "harness engineering is ongoing" land without
          resorting to a productivity claim the evidence would not support. */}
      <Typography variant="h5" fontWeight={700} sx={{ mt: 7, mb: 1 }}>
        {t("ClaudeHarnessHome.failuresTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
        {t("ClaudeHarnessHome.failuresLede")}
      </Typography>
      <Stack spacing={2}>
        {["f1", "f2", "f3", "f4"].map((k) => (
          <Paper
            key={k}
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `3px solid ${theme.palette.error.main}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, lineHeight: 1.45 }}>
              {t(`ClaudeHarnessHome.${k}Quote`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.65 }}>
              {t(`ClaudeHarnessHome.${k}Why`)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.primary.main, fontWeight: 700, fontFamily: "monospace" }}
            >
              {t(`ClaudeHarnessHome.${k}Module`)}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {/* OUTCOMES */}
      <Typography variant="h5" fontWeight={700} sx={{ mt: 7, mb: 2 }}>
        {t("ClaudeHarnessHome.outcomesTitle")}
      </Typography>
      <Stack spacing={1.25}>
        {["o1", "o2", "o3", "o4", "o5", "o6"].map((k) => (
          <Stack key={k} direction="row" spacing={1.5} alignItems="flex-start">
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: theme.palette.success.main, mt: "3px" }} />
            <Typography variant="body1" color="text.secondary" sx={{ flex: 1, lineHeight: 1.6 }}>
              {t(`ClaudeHarnessHome.${k}`)}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* HONEST SCOPE — deliberately says what the course is NOT, and carries the
          one measured number with its caveats attached rather than stripped. */}
      <Paper
        elevation={0}
        sx={{ p: 3, mt: 6, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t("ClaudeHarnessHome.scopeTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {t("ClaudeHarnessHome.scopeBody")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, lineHeight: 1.6 }}>
          {t("ClaudeHarnessHome.scopeMetric")}
        </Typography>
      </Paper>

      <Typography variant="h5" fontWeight={700} sx={{ mt: 6, mb: 2 }}>
        {t("ClaudeHarnessHome.modulesHeading")}
      </Typography>
      <Stack spacing={2}>
        {modules.map((m) => (
          <Paper
            key={m.slug}
            component={RouterLink}
            to={`/claude-harness/module/${m.slug}`}
            elevation={0}
            sx={{
              p: 3,
              display: "block",
              textDecoration: "none",
              color: "inherit",
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `3px solid ${m.locked ? theme.palette.divider : theme.palette.primary.main}`,
              borderRadius: 2,
              transition: "border-color .15s",
              "&:hover": { borderColor: theme.palette.primary.main },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, fontFamily: "monospace" }}
              >
                {m.number}
              </Typography>
              {/* THREE states, not two. Branching on `locked` alone labelled every
                  unlocked module "Free" — so a subscriber, and anyone who had just
                  paid $149, saw the whole paid course presented as free content.
                  `tier` is what distinguishes genuinely-free HRN.1 from a paid
                  module the viewer happens to own. */}
              {m.locked ? (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={t("ClaudeHarnessHome.lockedBadge")}
                  sx={{ height: 22 }}
                />
              ) : m.tier === "free" ? (
                <Chip size="small" variant="outlined" color="primary" label={t("ClaudeHarnessHome.freeBadge")} sx={{ height: 22 }} />
              ) : (
                <Chip size="small" variant="outlined" color="success" label={t("ClaudeHarnessHome.includedBadge")} sx={{ height: 22 }} />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto", fontFamily: "monospace" }}
              >
                ~{m.studyTime}
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700}>
              {m.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {m.tagline}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {first && (
        <Box sx={{ mt: 5 }}>
          <Button
            component={RouterLink}
            to={`/claude-harness/module/${first.slug}`}
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
          >
            {/* "Start FREE with HRN.1" is only true for someone who has not paid.
                Saying it to a buyer describes the course they just bought as free. */}
            {owns
              ? t("ClaudeHarnessHome.startCourse", { number: first.number })
              : t("ClaudeHarnessHome.startFree", { number: first.number })}
          </Button>
        </Box>
      )}

      {/* The purchase surface. Absent entirely until now: the SKU, the price and
          the entitlement all existed with nothing to trigger them. Shown only to
          people who do not already own the course — D-012 means an All-Access
          subscriber owns it, and offering them checkout is a double charge. */}
      {!owns && (
        <Box sx={{ mt: 5 }}>
          <HarnessAccessCTA />
        </Box>
      )}

      {/* The mirror image of the CTA above: owners get the living-harness log,
          non-owners get the buy button, and neither sees the other. `owns` is the
          same server-inferred signal, so the two can never both appear. */}
      {owns && <HarnessChangelog />}
    </Container>
  );
}
